import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "@/components/public/Seo";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Support from "@/pages/Support";
import Categories from "@/pages/public/Categories";
import HowItWorks from "@/pages/public/HowItWorks";
import About from "@/pages/public/About";
import Sell from "@/pages/public/Sell";
import Legal from "@/pages/public/Legal";

import CustomerLayout from "@/pages/customer/CustomerLayout";
import Home from "@/pages/customer/Home";
import StorePage from "@/pages/customer/StorePage";
import CartPage from "@/pages/customer/CartPage";
import Checkout from "@/pages/customer/Checkout";
import Orders from "@/pages/customer/Orders";
import OrderTrack from "@/pages/customer/OrderTrack";
import Profile from "@/pages/customer/Profile";

import MerchantApp from "@/pages/merchant/MerchantApp";
import CourierApp from "@/pages/courier/CourierApp";
import AdminApp from "@/pages/admin/AdminApp";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

const PUBLIC_PATHS = ["/", "/categories", "/how-it-works", "/about", "/sell", "/support", "/privacy", "/terms"];

function SupportPage() {
  const { t } = useTranslation();
  return <><Seo path="/support" title={`${t("support.title")} | Drivons Market`} description={t("support.subtitle")} /><Support /></>;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) return;
    document.head.querySelector('link[rel="canonical"]')?.remove();
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    document.title = "Drivons Market";
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/about" element={<About />} />
      <Route path="/sell" element={<Sell />} />
      <Route path="/privacy" element={<Legal kind="privacy" />} />
      <Route path="/terms" element={<Legal kind="terms" />} />

      <Route path="/app" element={<Protected roles={["customer"]}><CustomerLayout /></Protected>}>
        <Route index element={<Home />} />
        <Route path="store/:sid" element={<StorePage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="checkout/:sid" element={<Checkout />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:oid" element={<OrderTrack />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      <Route path="/merchant/*" element={<Protected roles={["merchant"]}><MerchantApp /></Protected>} />
      <Route path="/courier/*" element={<Protected roles={["courier"]}><CourierApp /></Protected>} />
      <Route path="/admin/*" element={<Protected roles={["admin"]}><AdminApp /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
